import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PhoneCall, Plus, Trash2, Check, X, PackageCheck, Upload, FileText, MapPin, Send, Truck, Search, Link2, Copy, MessageSquare } from "lucide-react";
import { resolvePublicLink } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const emptyForm = { nom: "", telephone: "", email: "", adresse: "", ville: "", code_postal: "", note: "", precommande: false, date_rappel: "", items: [] };
const emptyItem = { sku: "", modele: "", couleur: "", quantite: 1 };

const STATUT_LABELS = { a_rappeler: "À rappeler", rappele: "Rappelé", converti: "Converti", annule: "Annulé" };
const STATUT_COLORS = { a_rappeler: "bg-amber-100 text-amber-800", rappele: "bg-blue-100 text-blue-800", converti: "bg-green-100 text-green-800", annule: "bg-slate-100 text-slate-500" };

const Rappels = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [produits, setProduits] = useState([]);
  const [rappels, setRappels] = useState([]);
  const [params, setParams] = useState(null);  const [reservations, setReservations] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all"); // "all" | "precommande" | "rappel"
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);

  const fetchAll = async () => {
    try {
      const [p, r, s, c, pa] = await Promise.all([
        axios.get(`${API}/produits`, { headers }),
        axios.get(`${API}/rappels`, { headers }),
        axios.get(`${API}/rappels/reserved-summary`, { headers }),
        axios.get(`${API}/clients`, { headers }),
        axios.get(`${API}/parametres`, { headers }),
      ]);
      setProduits(p.data);
      setRappels(r.data);
      setReservations(s.data?.reservations || []);
      setClients(c.data || []);
      setParams(pa.data);
    } catch { toast.error("Erreur de chargement"); }
  };

  const filteredClients = clientSearch.trim().length >= 1
    ? clients.filter(c => {
        const q = clientSearch.toLowerCase();
        return (c.nom || "").toLowerCase().includes(q)
          || (c.telephone || "").includes(q)
          || (c.email || "").toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  const pickClient = (c) => {
    setForm({
      ...form,
      nom: c.nom || "",
      telephone: c.telephone || "",
      email: c.email || "",
      adresse: c.adresse || "",
      ville: c.ville || "",
      code_postal: c.code_postal || "",
    });
    setClientSearch(c.nom || "");
    setClientDropdownOpen(false);
    toast.success(`Client sélectionné : ${c.nom} (${c.nombre_commandes || 0} commande(s))`);
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickProduct = (idx, val) => {
    const prod = produits.find((x) => x.id === val);
    if (!prod) return;
    const items = [...form.items];
    items[idx] = { ...items[idx], produit_id: prod.id, sku: prod.sku, modele: prod.modele, couleur: prod.couleur };
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, patch) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, items });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom) { toast.error("Le nom est obligatoire"); return; }
    const validItems = (form.items || []).filter(it => it.sku || it.modele);
    if (validItems.length === 0) { toast.error("Ajoute au moins un produit"); return; }
    setSaving(true);
    try {
      // Utilise l'endpoint import-bulk pour créer plusieurs précommandes en une seule requête
      const lignes = validItems.map(it => ({
        nom: form.nom,
        telephone: form.telephone,
        email: form.email,
        adresse: form.adresse,
        ville: form.ville,
        code_postal: form.code_postal,
        sku: it.sku || "",
        modele: it.modele || "",
        couleur: it.couleur || "",
        quantite: parseInt(it.quantite) || 1,
        note: form.note || "",
      }));
      await axios.post(`${API}/rappels/import-bulk`, {
        lignes,
        precommande: form.precommande,
      }, { headers });
      toast.success(`✅ ${validItems.length} précommande(s) créée(s) pour ${form.nom}`);
      setForm(emptyForm);
      setClientSearch("");
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const setStatut = async (r, statut) => {
    try {
      const { id, created_at, stock_dispo, _id, ...rest } = r;
      await axios.put(`${API}/rappels/${r.id}`, { ...rest, statut }, { headers });
      toast.success(`Marqué « ${STATUT_LABELS[statut]} »`);
      fetchAll();
    } catch { toast.error("Erreur de mise à jour"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce rappel ?")) return;
    try { await axios.delete(`${API}/rappels/${id}`, { headers }); toast.success("Supprimé"); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  // Import en vrac
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSku, setBulkSku] = useState("");
  const [bulkPrecommande, setBulkPrecommande] = useState(true);
  const [bulkImporting, setBulkImporting] = useState(false);

  // Parseur intelligent : détecte modèle + couleur + quantité + tel + email + adresse par ligne
  // Peut créer PLUSIEURS entrées par ligne (ex: "4x Milan White + 4x Milan Orange")
  const COULEUR_SYN = { black: "Noir", noir: "Noir", noire: "Noir", white: "Blanc", blanc: "Blanc", blanche: "Blanc", beige: "Beige", sand: "Sand", grey: "Gris", gray: "Gris", gris: "Gris", grise: "Gris", rose: "Rose", pink: "Rose", transparent: "Transparent", clear: "Transparent", vert: "Vert", green: "Vert", canard: "Vert canard", turquoise: "Turquoise", bleu: "Bleu", blue: "Bleu", rouge: "Rouge", red: "Rouge", jaune: "Jaune", yellow: "Jaune", orange: "Orange", marron: "Marron", brown: "Marron", sage: "Vert sauge", sauge: "Vert sauge", fume: "Transparent fumé", "fumé": "Transparent fumé", opaque: "opaque" };
  const parseBulkLines = (raw) => {
    const modeleMap = new Map();
    produits.forEach(p => modeleMap.set((p.modele || "").toLowerCase().trim(), p));

    const parseProduitPart = (chunk) => {
      const ps = chunk.toLowerCase();
      const psWords = ps.match(/[a-zA-Zéèêëàâîïôùûç]+/g) || [];
      let quantite = 1, modele = "", couleur = "", sku = "";
      const qtyMatch = ps.match(/(\d{1,3})\s*x\s+/);
      if (qtyMatch) quantite = parseInt(qtyMatch[1]) || 1;
      const modeleFound = psWords.find(w => modeleMap.has(w));
      if (modeleFound) modele = modeleMap.get(modeleFound).modele;
      const TWO_WORD_COLORS = [["vert", "canard", "Vert canard"], ["vert", "sauge", "Vert sauge"], ["bleu", "marine", "Bleu marine"], ["gris", "clair", "Gris clair"], ["gris", "foncé", "Gris foncé"], ["gris", "fonce", "Gris foncé"], ["dark", "grey", "Gris foncé"], ["light", "grey", "Gris clair"], ["noir", "opaque", "Noir opaque"], ["blanc", "opaque", "Blanc opaque"], ["transparent", "fumé", "Transparent fumé"], ["transparent", "fume", "Transparent fumé"], ["transparent", "bleu", "Transparent bleu"]];
      for (const [w1, w2, label] of TWO_WORD_COLORS) {
        if (ps.includes(`${w1} ${w2}`)) { couleur = label; break; }
      }
      if (!couleur) {
        for (const w of psWords) { if (w !== modeleFound && COULEUR_SYN[w] && COULEUR_SYN[w] !== "opaque") { couleur = COULEUR_SYN[w]; break; } }
      }
      if (modele) {
        const prod = produits.find(p => (p.modele || "").toLowerCase().trim() === modele.toLowerCase() && (couleur ? (p.couleur || "").toLowerCase() === couleur.toLowerCase() : true));
        if (prod) sku = prod.sku;
      }
      return { quantite, modele, couleur, sku };
    };

    const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);
    const allEntries = [];
    for (const line of lines) {
      const parts = line.split(/[|;\t]+|\s{2,}/).map(p => p.trim()).filter(Boolean);
      const nom = parts[0] || "";
      let telephone = "", email = "", adresse = "", ville = "", code_postal = "";

      // Trouve le segment produit (contient "Nx")
      let produitSegment = "";
      for (const p of parts) {
        if (/\d+\s*x\s+/i.test(p) && !/^\d{8,}/.test(p.replace(/\s/g, ""))) { produitSegment = p; break; }
      }
      if (!produitSegment) produitSegment = parts[parts.length - 1] || "";

      // Extraction contact
      for (let i = 1; i < parts.length; i++) {
        const p = parts[i];
        if (p === produitSegment) continue;
        if (p.includes("@") && !email) { email = p; continue; }
        const digits = p.replace(/[^0-9+]/g, "");
        if (digits.length >= 8 && !telephone) { telephone = digits; continue; }
        if (/^\d{4}$/.test(p) && !code_postal) {
          code_postal = p;
          const next = parts[i + 1];
          if (next && !/^\d/.test(next) && next.length < 30 && !next.includes("@") && next !== produitSegment) ville = next;
          continue;
        }
        if (!adresse && /\d/.test(p) && /[a-zA-Zéè]/.test(p) && p.length > 6 && !p.includes("@") && !/^\d+\s*x\s/.test(p)) adresse = p;
      }

      // Split multi-produits sur '+', 'et', '&'
      const chunks = produitSegment.split(/\s*[+&]\s*|\s+et\s+/i).map(c => c.trim()).filter(Boolean);
      const parsed = chunks.map(parseProduitPart).filter(p => p.modele || p.couleur || p.sku);
      if (parsed.length === 0) parsed.push({ quantite: 1, modele: "", couleur: "", sku: "" });

      for (const pp of parsed) {
        allEntries.push({
          nom, telephone, email, adresse, ville, code_postal,
          quantite: pp.quantite, sku: pp.sku, modele: pp.modele, couleur: pp.couleur, note: "",
        });
      }
    }
    return allEntries;
  };

  const submitBulk = async () => {
    if (!bulkText.trim()) { toast.error("Colle au moins une ligne"); return; }
    setBulkImporting(true);
    try {
      const lignes = parseBulkLines(bulkText);
      const prod = produits.find((p) => p.sku === bulkSku);
      const res = await axios.post(`${API}/rappels/import-bulk`, {
        texte: bulkText,
        lignes,
        sku_defaut: bulkSku || "",
        modele_defaut: prod?.modele || "",
        couleur_defaut: prod?.couleur || "",
        precommande: bulkPrecommande,
      }, { headers });
      toast.success(`${res.data.importes} ${bulkPrecommande ? "précommande(s)" : "rappel(s)"} importé(s) ✅`);
      setBulkOpen(false);
      setBulkText("");
      setBulkSku("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur import");
    } finally { setBulkImporting(false); }
  };

  // SMS "Stock arrivé" — utilise le template éditable dans Paramètres
  const sendStockDispoSMS = async (r) => {
    if (!r.telephone) { toast.error("Pas de téléphone"); return; }
    const prenom = (r.nom || "").split(" ")[0] || "client";
    try {
      // Lien RDV : Paramètres → sinon RDV config → sinon origine
      const lienRdvBase = resolvePublicLink(params?.lien_rdv, "/prendre-rdv");
      const lienRdvRetrait = `${lienRdvBase.split("?")[0]}?type=retrait`;

      const res = await axios.post(`${API}/message-templates/sms_stock_arrive/preview`, {
        prenom,
        nom: r.nom || "",
        modele: r.modele || "",
        couleur: r.couleur || "",
        quantite: r.quantite || 1,
        nom_societe: "TopCocoon",
        lien_rdv_retrait: lienRdvRetrait,
      }, { headers });
      const msg = res.data.body || "";
      window.location.href = `sms:${r.telephone}?body=${encodeURIComponent(msg)}`;
    } catch { toast.error("Erreur génération message"); }
  };

  // Copier le message client (lien de précommande) — utilise le template sms_lien_precommande
  const copyPrecommandeMessage = async (r) => {
    const prenom = (r?.nom || "").split(" ")[0] || "client";
    try {
      const lienPre = resolvePublicLink(params?.lien_precommande, "/precommande");
      const res = await axios.post(`${API}/message-templates/sms_lien_precommande/preview`, {
        prenom,
        lien_precommande: lienPre,
        nom_societe: "TopCocoon",
      }, { headers });
      const msg = res.data.body || "";
      await navigator.clipboard.writeText(msg);
      toast.success("Message copié dans le presse-papier !");
    } catch { toast.error("Erreur génération message"); }
  };

  // Édition inline d'une précommande
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const openEdit = (r) => { setEditTarget({ ...r }); setEditOpen(true); };
  const saveEdit = async () => {
    if (!editTarget) return;
    try {
      const { id, stock_dispo, position_fifo, created_at, ordre_import, livraison_id, ...payload } = editTarget;
      await axios.put(`${API}/rappels/${id}`, payload, { headers });
      toast.success("Modifié");
      setEditOpen(false);
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  // Sélection multiple + conversion tournée
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const [tourneesExistantes, setTourneesExistantes] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertCap, setConvertCap] = useState(100);
  const [convertOptimiser, setConvertOptimiser] = useState(true);
  const [convertDate, setConvertDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Supprimer ${selected.size} précommande(s) ? Cette action est irréversible.`)) return;
    setDeleting(true);
    try {
      await Promise.all(Array.from(selected).map(id => axios.delete(`${API}/rappels/${id}`, { headers })));
      toast.success(`✅ ${selected.size} précommande(s) supprimée(s)`);
      setSelected(new Set());
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur suppression"); }
    finally { setDeleting(false); }
  };

  const deleteAllShown = async () => {
    const currentShown = rappels.filter((r) => filter === "all" || r.statut === filter);
    const ids = currentShown.map(r => r.id);
    if (ids.length === 0) return;
    if (!window.confirm(`⚠️ Supprimer TOUTES les ${ids.length} précommande(s) affichée(s) ? Action irréversible.`)) return;
    setDeleting(true);
    try {
      await Promise.all(ids.map(id => axios.delete(`${API}/rappels/${id}`, { headers })));
      toast.success(`✅ ${ids.length} précommande(s) supprimée(s)`);
      setSelected(new Set());
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur suppression"); }
    finally { setDeleting(false); }
  };

  const toggleSelectAllShown = () => {
    const currentShown = rappels.filter((r) => filter === "all" || r.statut === filter);
    const shownIds = currentShown.filter(r => r.statut === "a_rappeler").map(r => r.id);
    if (selected.size === shownIds.length && shownIds.every(id => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(shownIds));
    }
  };

  const loadTournees = async () => {
    try {
      const res = await axios.get(`${API}/tournees`, { headers });
      setTourneesExistantes((res.data || []).filter(t => t.statut !== "terminee" && t.statut !== "annulee"));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadTournees(); }, []); // eslint-disable-line

  const convertSelected = async () => {
    if (selected.size === 0) { toast.error("Sélectionne au moins une précommande"); return; }
    setConverting(true);
    try {
      const res = await axios.post(`${API}/rappels/convert-to-livraisons`, {
        rappel_ids: Array.from(selected),
        creer_tournee: true,
        capacite_max: convertCap,
        optimiser_geo: convertOptimiser,
        date_tournee: convertDate,
      }, { headers });
      const nb = res.data.nb_tournees || 0;
      toast.success(`✅ ${res.data.livraisons_creees} livraison(s) → ${nb} tournée(s) créée(s)`, { duration: 10000 });
      setSelected(new Set());
      setConvertOpen(false);
      fetchAll();
      loadTournees();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur conversion"); }
    finally { setConverting(false); }
  };

  // Ajout à tournée existante (une seule précommande)
  const [addTourOpen, setAddTourOpen] = useState(false);
  const [addTourTarget, setAddTourTarget] = useState(null);  // {rappel, tournee_id}
  const openAddTour = (r) => { setAddTourTarget({ rappel: r, tournee_id: "" }); setAddTourOpen(true); loadTournees(); };
  const submitAddTour = async (force = false) => {
    if (!addTourTarget?.rappel || !addTourTarget?.tournee_id) { toast.error("Choisis une tournée"); return; }
    try {
      const res = await axios.post(`${API}/rappels/add-to-tournee`, {
        rappel_ids: [addTourTarget.rappel.id],
        tournee_id: addTourTarget.tournee_id,
        force,
      }, { headers });
      if (res.data.warning) {
        if (window.confirm(`⚠️ ${res.data.message}\n(${res.data.chaises_actuelles} + ${res.data.chaises_a_ajouter} = ${res.data.total} chaises)`)) {
          return submitAddTour(true);
        }
        return;
      }
      toast.success(`✅ Ajoutée à la tournée (${res.data.total_chaises} chaises)${res.data.depassement ? " ⚠️" : ""}`);
      setAddTourOpen(false);
      fetchAll();
      loadTournees();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  const shown = rappels.filter((r) => {
    if (filter !== "all" && r.statut !== filter) return false;
    if (typeFilter === "precommande" && !r.precommande) return false;
    if (typeFilter === "rappel" && r.precommande) return false;
    return true;
  });
  const dispoCount = rappels.filter((r) => r.stock_dispo).length;

  return (
    <div className="space-y-8" data-testid="rappels-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
          <PhoneCall className="w-7 h-7 text-indigo-600" /> À rappeler / Précommandes
        </h1>
        <p className="text-slate-500 mt-1">Suivez les clients à recontacter et les précommandes en attente de stock.</p>

        {/* Lien public de précommande — partage rapide */}
        <Card className="border-indigo-200 bg-indigo-50/40 mt-4" data-testid="precommande-share-card">
          <CardContent className="py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <Link2 className="w-5 h-5 text-indigo-600 shrink-0" />
            <code className="flex-1 text-xs sm:text-sm text-indigo-800 break-all" data-testid="precommande-public-link">
              {resolvePublicLink(params?.lien_precommande, "/precommande")}
            </code>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(resolvePublicLink(params?.lien_precommande, "/precommande"));
                  toast.success("Lien copié !");
                }}
                data-testid="precommande-copy-link-btn"
              >
                <Copy className="w-4 h-4 mr-1" />Copier
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(resolvePublicLink(params?.lien_precommande, "/precommande"), "_blank")}
                data-testid="precommande-open-link-btn"
              >
                Ouvrir
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-3">
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="bulk-import-btn">
            <Upload className="w-4 h-4 mr-2" />Importer précommandes en vrac
          </Button>
        </div>
      </div>

      {reservations.length > 0 && (
        <Card className="border-indigo-200 bg-indigo-50/60" data-testid="reservations-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-indigo-900">
              <PackageCheck className="w-5 h-5" />Chaises réservées (précommandes + livraisons en attente)
              <Badge className="bg-indigo-600 text-white ml-2">{reservations.reduce((s, r) => s + r.chaises_reservees, 0)} chaises</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex flex-wrap gap-2">
              {reservations.map((r, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded-lg border ${r.existe_en_stock ? "bg-white border-indigo-200" : "bg-amber-50 border-amber-300"}`} data-testid={`reservation-${i}`}>
                  <div className="font-semibold text-slate-800">{r.modele || "?"} {r.couleur || "?"}</div>
                  <div className="text-xs text-slate-600">
                    <strong className="text-indigo-700">{r.chaises_reservees}</strong> chaises · {r.nb_clients} client(s)
                    {r.stock_actuel !== null && r.stock_actuel !== undefined && (
                      <span className="ml-2 text-slate-500">· stock: {r.stock_actuel}</span>
                    )}
                    {!r.existe_en_stock && <span className="ml-2 text-amber-700 italic">à créer</span>}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    {r.source_precommande > 0 && <span className="mr-2">📞 précommandes : {r.source_precommande}</span>}
                    {r.source_livraison > 0 && <span>🚚 livraisons en attente : {r.source_livraison}</span>}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Astuce : les livraisons en attente issues de précommandes converties comptent encore comme réservées.
              Pour les libérer, va dans <strong>Livraisons</strong> et supprime/annule les concernées.
            </p>
          </CardContent>
        </Card>
      )}

      {dispoCount > 0 && (
        <Card className="border-green-300 bg-green-50/60" data-testid="rappels-stock-alert">
          <CardContent className="py-4 flex items-center gap-3 text-green-800">
            <PackageCheck className="w-5 h-5" />
            <span className="font-medium">{dispoCount} précommande(s) à contacter : le stock est de nouveau disponible !</span>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Nouveau rappel</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            {/* Recherche client existant */}
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
              <Label className="text-slate-700 font-semibold text-sm flex items-center gap-2">
                <Search className="w-4 h-4" />Client existant (facultatif)
              </Label>
              <div className="relative">
                <Input
                  value={clientSearch}
                  onChange={(e) => { setClientSearch(e.target.value); setClientDropdownOpen(true); }}
                  onFocus={() => setClientDropdownOpen(true)}
                  placeholder="Tape un nom, un tél ou un email…"
                  className="bg-white"
                  data-testid="client-search-input"
                />
                {clientDropdownOpen && filteredClients.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {filteredClients.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => pickClient(c)}
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-0"
                        data-testid={`client-option-${c.id}`}
                      >
                        <div className="font-semibold text-slate-900">{c.nom}</div>
                        <div className="text-xs text-slate-600 flex gap-2 flex-wrap">
                          {c.telephone && <span>📞 {c.telephone}</span>}
                          {c.ville && <span>📍 {c.ville}</span>}
                          <span className="text-indigo-700 font-medium">🛒 {c.nombre_commandes || 0} commande(s)</span>
                          {c.total_depense > 0 && <span className="text-emerald-700">💰 {c.total_depense.toFixed(0)}€</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {clientSearch && filteredClients.length === 0 && (
                <p className="text-xs text-slate-500">Aucun client trouvé — remplis le formulaire ci-dessous manuellement.</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nom *</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Dupont Marie" data-testid="rappel-nom-input" /></div>
              <div className="space-y-2"><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="+32 470 12 34 56" data-testid="rappel-tel-input" /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rappel-email-input" /></div>
              <div className="space-y-2"><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} placeholder="Rue…" data-testid="rappel-adresse-input" /></div>
              <div className="space-y-2"><Label>Code postal</Label><Input value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} placeholder="1000" data-testid="rappel-cp-input" /></div>
              <div className="space-y-2"><Label>Ville</Label><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} placeholder="Bruxelles" data-testid="rappel-ville-input" /></div>
              <div className="space-y-2"><Label>Date de rappel</Label><Input type="date" value={form.date_rappel} onChange={(e) => setForm({ ...form, date_rappel: e.target.value })} data-testid="rappel-date-input" /></div>
            </div>

            {/* Éditeur multi-produits */}
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-emerald-900 font-semibold text-sm flex items-center gap-2">
                  <PackageCheck className="w-4 h-4" />Produits à précommander ({form.items.length})
                </Label>
                <Button type="button" size="sm" onClick={addItem} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-1" />Ajouter un produit
                </Button>
              </div>
              {form.items.length === 0 && (
                <p className="text-xs text-slate-500 italic">Aucun produit — clique &quot;Ajouter un produit&quot; pour commencer.</p>
              )}
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-emerald-100" data-testid={`item-row-${idx}`}>
                  <div className="col-span-5">
                    <Label className="text-xs">Produit</Label>
                    <Select value={it.sku || "none"} onValueChange={(v) => {
                      if (v === "none") { updateItem(idx, { sku: "", modele: it.modele, couleur: it.couleur }); return; }
                      const p = produits.find(x => x.sku === v);
                      updateItem(idx, { sku: v, modele: p?.modele || "", couleur: p?.couleur || "" });
                    }}>
                      <SelectTrigger className="h-9" data-testid={`item-sku-${idx}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Produit à créer —</SelectItem>
                        {produits.map(p => <SelectItem key={p.sku} value={p.sku}>{p.modele} {p.couleur} (stock {p.stock_restant})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Modèle</Label>
                    <Input value={it.modele || ""} onChange={(e) => updateItem(idx, { modele: e.target.value })} placeholder="Milan" className="h-9" data-testid={`item-modele-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Couleur</Label>
                    <Input value={it.couleur || ""} onChange={(e) => updateItem(idx, { couleur: e.target.value })} placeholder="Rose" className="h-9" data-testid={`item-couleur-${idx}`} />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Qté</Label>
                    <Input type="number" min="1" value={it.quantite} onChange={(e) => updateItem(idx, { quantite: parseInt(e.target.value) || 1 })} className="h-9" data-testid={`item-qty-${idx}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)} className="text-red-600 hover:bg-red-50 h-9 w-9 p-0" data-testid={`item-remove-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2"><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Détails de la demande…" data-testid="rappel-note-input" /></div>
            <div className="flex items-center gap-2">
              <Checkbox id="precommande" checked={form.precommande} onCheckedChange={(v) => setForm({ ...form, precommande: !!v })} data-testid="rappel-precommande-check" />
              <Label htmlFor="precommande" className="cursor-pointer">Précommande (alerte quand le stock revient)</Label>
            </div>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rappel-submit-btn">
              <Plus className="w-4 h-4 mr-2" />{saving ? "Ajout…" : "Ajouter"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Liste des rappels{selected.size > 0 ? ` — ${selected.size} sélectionné(s)` : ""}</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {selected.size > 0 && (
              <>
                <Button onClick={() => setConvertOpen(true)} disabled={converting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-selected-btn">
                  <Truck className="w-4 h-4 mr-2" />Créer tournées optimisées ({selected.size})
                </Button>
                <Button onClick={deleteSelected} disabled={deleting} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-selected-btn">
                  <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selected.size})
                </Button>
              </>
            )}
            {shown.length > 0 && (
              <Button onClick={deleteAllShown} disabled={deleting} variant="outline" className="border-red-400 text-red-800 hover:bg-red-100" data-testid="delete-all-shown-btn">
                <Trash2 className="w-4 h-4 mr-2" />Tout supprimer ({shown.length})
              </Button>
            )}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-44" data-testid="rappel-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="precommande">📦 Précommandes</SelectItem>
                <SelectItem value="rappel">📞 Simples rappels</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="rappel-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="a_rappeler">À rappeler</SelectItem>
                <SelectItem value="rappele">Rappelé</SelectItem>
                <SelectItem value="converti">Converti</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {shown.length === 0 ? (
            <p className="text-slate-400 text-sm">Aucun rappel.</p>
          ) : (() => {
            const precoms = shown.filter(r => r.precommande);
            const rappels = shown.filter(r => !r.precommande);
            const renderRow = (r) => (
                  <TableRow key={r.id} data-testid={`rappel-row-${r.id}`} className={r.stock_dispo ? "bg-green-50/50" : ""}>
                    <TableCell>
                      {r.statut === "a_rappeler" && (
                        <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid={`select-${r.id}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{r.nom}</div>
                      <div className="text-xs text-slate-500 break-words">{r.telephone}{r.email ? ` · ${r.email}` : ""}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.adresse ? (
                        <div className="text-slate-700">
                          <MapPin className="w-3 h-3 inline mr-1 text-slate-400" />
                          {r.adresse}{r.code_postal ? `, ${r.code_postal}` : ""}{r.ville ? ` ${r.ville}` : ""}
                        </div>
                      ) : <span className="text-xs text-orange-500 italic">Adresse manquante</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{r.modele ? `${r.quantite}x ${r.modele} ${r.couleur}` : `${r.quantite} unité(s)`}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.precommande && r.position_fifo && (
                          <Badge className="text-xs bg-indigo-600" data-testid={`fifo-${r.id}`}>#{r.position_fifo} en file</Badge>
                        )}
                        {r.precommande && !r.position_fifo && <Badge variant="outline" className="text-xs">Précommande</Badge>}
                        {!r.precommande && <Badge variant="outline" className="text-xs border-amber-400 text-amber-700 bg-amber-50">Simple rappel</Badge>}
                        {r.stock_dispo && <Badge className="bg-green-600 text-xs">Stock dispo</Badge>}
                        {r.source === "formulaire_public" && <Badge className="text-xs bg-emerald-500">🌐 Public</Badge>}
                      </div>
                    </TableCell>
                    <TableCell><Badge className={`${STATUT_COLORS[r.statut]} border-0`}>{STATUT_LABELS[r.statut] || r.statut}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} title="Modifier" data-testid={`rappel-edit-${r.id}`}><FileText className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" onClick={() => copyPrecommandeMessage(r)} title="Copier le message client (lien de précommande)" data-testid={`copy-message-${r.id}`}><MessageSquare className="w-4 h-4" /></Button>
                      {r.stock_dispo && r.telephone && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => sendStockDispoSMS(r)} title="SMS Stock arrivé" data-testid={`sms-stock-${r.id}`}><Send className="w-4 h-4" /></Button>
                      )}
                      {r.statut === "a_rappeler" && (
                        <Button size="sm" variant="outline" className="border-blue-400 text-blue-700" onClick={() => openAddTour(r)} title="Ajouter à tournée existante" data-testid={`add-tour-${r.id}`}><Truck className="w-4 h-4" /></Button>
                      )}
                      {r.statut !== "rappele" && <Button size="sm" variant="outline" onClick={() => setStatut(r, "rappele")} title="Rappelé" data-testid={`rappel-done-${r.id}`}><PhoneCall className="w-4 h-4" /></Button>}
                      {r.statut !== "converti" && <Button size="sm" variant="outline" className="text-green-600" onClick={() => setStatut(r, "converti")} title="Converti" data-testid={`rappel-converti-${r.id}`}><Check className="w-4 h-4" /></Button>}
                      {r.statut !== "annule" && <Button size="sm" variant="outline" onClick={() => setStatut(r, "annule")} title="Annuler" data-testid={`rappel-annule-${r.id}`}><X className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(r.id)} data-testid={`rappel-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
            );
            const Section = ({ title, emoji, color, items, testid }) => (
              items.length > 0 && (
                <div className="mb-4" data-testid={testid}>
                  <div className={`sticky top-0 z-10 flex items-center gap-2 px-3 py-2 rounded-t-lg ${color} font-bold text-sm border-b`}>
                    <span className="text-lg">{emoji}</span>
                    <span>{title}</span>
                    <span className="ml-auto text-xs bg-white/60 px-2 py-0.5 rounded-full">{items.length}</span>
                  </div>
                  <div className="border rounded-b-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Client</TableHead><TableHead>Adresse</TableHead><TableHead>Produit</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>{items.map(renderRow)}</TableBody>
                    </Table>
                  </div>
                </div>
              )
            );
            return (
              <>
                <Section title="Précommandes" emoji="📦" color="bg-indigo-100 text-indigo-900" items={precoms} testid="section-precommandes" />
                <Section title="À rappeler" emoji="📞" color="bg-amber-100 text-amber-900" items={rappels} testid="section-rappels" />
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Dialog édition inline */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-rappel-dialog">
          <DialogHeader><DialogTitle>Modifier la précommande</DialogTitle></DialogHeader>
          {editTarget && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom</Label><Input value={editTarget.nom || ""} onChange={(e) => setEditTarget({ ...editTarget, nom: e.target.value })} data-testid="edit-nom" /></div>
                <div><Label>Téléphone</Label><Input value={editTarget.telephone || ""} onChange={(e) => setEditTarget({ ...editTarget, telephone: e.target.value })} data-testid="edit-tel" /></div>
                <div><Label>Email</Label><Input value={editTarget.email || ""} onChange={(e) => setEditTarget({ ...editTarget, email: e.target.value })} data-testid="edit-email" /></div>
                <div><Label>Quantité</Label><Input type="number" value={editTarget.quantite || 1} onChange={(e) => setEditTarget({ ...editTarget, quantite: parseInt(e.target.value) || 1 })} data-testid="edit-qte" /></div>
              </div>
              <div><Label>Adresse</Label><Input value={editTarget.adresse || ""} onChange={(e) => setEditTarget({ ...editTarget, adresse: e.target.value })} placeholder="Rue et numéro" data-testid="edit-adresse" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code postal</Label><Input value={editTarget.code_postal || ""} onChange={(e) => setEditTarget({ ...editTarget, code_postal: e.target.value })} data-testid="edit-cp" /></div>
                <div><Label>Ville</Label><Input value={editTarget.ville || ""} onChange={(e) => setEditTarget({ ...editTarget, ville: e.target.value })} data-testid="edit-ville" /></div>
              </div>
              <div>
                <Label>SKU (optionnel — laisser vide si le produit n&apos;existe pas encore)</Label>
                <Select value={editTarget.sku || "none"} onValueChange={(v) => {
                  if (v === "none") { setEditTarget({ ...editTarget, sku: "" }); return; }
                  const p = produits.find(x => x.sku === v);
                  setEditTarget({ ...editTarget, sku: v, modele: p?.modele || editTarget.modele, couleur: p?.couleur || editTarget.couleur });
                }}>
                  <SelectTrigger data-testid="edit-sku"><SelectValue placeholder="Choisir un SKU existant…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun SKU (produit à venir) —</SelectItem>
                    {produits.map(p => <SelectItem key={p.sku} value={p.sku}>{p.modele} {p.couleur} (stock {p.stock_restant})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Modèle</Label><Input value={editTarget.modele || ""} onChange={(e) => setEditTarget({ ...editTarget, modele: e.target.value })} placeholder="ex : Milan" data-testid="edit-modele" /></div>
                <div><Label>Couleur</Label><Input value={editTarget.couleur || ""} onChange={(e) => setEditTarget({ ...editTarget, couleur: e.target.value })} placeholder="ex : Blanc, Vert canard…" data-testid="edit-couleur" /></div>
              </div>
              <div><Label>Note</Label><Textarea value={editTarget.note || ""} onChange={(e) => setEditTarget({ ...editTarget, note: e.target.value })} data-testid="edit-note" /></div>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border">
                <Checkbox
                  id="edit-precommande"
                  checked={!!editTarget.precommande}
                  onCheckedChange={(v) => setEditTarget({ ...editTarget, precommande: !!v })}
                  data-testid="edit-precommande"
                />
                <Label htmlFor="edit-precommande" className="cursor-pointer text-sm">
                  📦 Précommande <span className="text-slate-400">(décoché = 📞 simple rappel)</span>
                </Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={saveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conversion → tournées optimisées */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-w-lg" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-emerald-600" />Créer tournées optimisées</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-emerald-50 rounded text-sm text-emerald-800">
              <strong>{selected.size}</strong> précommande(s) sélectionnée(s) — total ≈ <strong>{Array.from(selected).reduce((s, id) => { const r = rappels.find(x => x.id === id); return s + (r?.quantite || 1); }, 0)}</strong> chaises
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Capacité max / tournée (chaises)</Label>
                <Input type="number" min="1" value={convertCap} onChange={(e) => setConvertCap(parseInt(e.target.value) || 100)} data-testid="convert-cap-input" />
              </div>
              <div>
                <Label>Date de tournée</Label>
                <Input type="date" value={convertDate} onChange={(e) => setConvertDate(e.target.value)} data-testid="convert-date-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="opt-geo" checked={convertOptimiser} onCheckedChange={(v) => setConvertOptimiser(!!v)} data-testid="convert-geo-check" />
              <Label htmlFor="opt-geo" className="cursor-pointer">Grouper par proximité géographique (recommandé)</Label>
            </div>
            <p className="text-xs text-slate-500">Les adresses seront géocodées automatiquement. Les livraisons sont regroupées par zone jusqu&apos;à atteindre la capacité, puis l&apos;ordre est optimisé via OSRM.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)} disabled={converting}>Annuler</Button>
            <Button onClick={convertSelected} disabled={converting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="convert-submit-btn">
              {converting ? "Création…" : "Créer les tournées"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ajout à tournée existante */}
      <Dialog open={addTourOpen} onOpenChange={setAddTourOpen}>
        <DialogContent className="max-w-md" data-testid="add-tour-dialog">
          <DialogHeader><DialogTitle>Ajouter à une tournée existante</DialogTitle></DialogHeader>
          {addTourTarget?.rappel && (
            <div className="space-y-3">
              <div className="text-sm p-2 bg-slate-50 rounded">
                <strong>{addTourTarget.rappel.nom}</strong> — {addTourTarget.rappel.quantite || 1}x {addTourTarget.rappel.modele} {addTourTarget.rappel.couleur}
              </div>
              <div>
                <Label>Tournée cible</Label>
                <Select value={addTourTarget.tournee_id} onValueChange={(v) => setAddTourTarget({ ...addTourTarget, tournee_id: v })}>
                  <SelectTrigger data-testid="add-tour-select"><SelectValue placeholder="Choisir une tournée…" /></SelectTrigger>
                  <SelectContent>
                    {tourneesExistantes.length === 0 && <div className="text-xs text-slate-500 p-2">Aucune tournée disponible</div>}
                    {tourneesExistantes.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nom} · {t.date} · {(t.livraisons_ids || []).length} arrêts
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">Un avertissement s&apos;affichera si la capacité de 100 chaises est dépassée.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTourOpen(false)}>Annuler</Button>
            <Button onClick={() => submitAddTour(false)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-tour-submit">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Import en vrac — même design que Livraisons Import intelligent */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Import intelligent — Précommandes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Sélecteur produit (appliqué à toutes les lignes — stock partagé) */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Label className="text-amber-900 font-semibold text-sm mb-2 block">🎯 Produit fallback (optionnel — utilisé uniquement si aucun modèle détecté sur la ligne)</Label>
              <Select value={bulkSku} onValueChange={setBulkSku}>
                <SelectTrigger data-testid="bulk-sku-select" className="bg-white"><SelectValue placeholder="— Aucun (auto-détection par ligne) —" /></SelectTrigger>
                <SelectContent>
                  {produits.map(p => (
                    <SelectItem key={p.sku} value={p.sku}>{p.modele} {p.couleur} — stock {p.stock_restant}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-amber-700 mt-2">Chaque ligne peut contenir son propre modèle (ex : « Milan Black », « Ghost Transparent »). Le SKU est auto-détecté par ligne. Ce fallback ne s&apos;applique qu&apos;aux lignes sans modèle identifié.</p>
            </div>

            {/* Type d'import (précommande vs simple rappel) */}
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <Label className="text-indigo-900 font-semibold text-sm mb-2 block">🏷️ Type d&apos;import (appliqué à toutes les lignes)</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setBulkPrecommande(true)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${bulkPrecommande ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-300"}`}
                  data-testid="bulk-type-precommande"
                >
                  📦 Précommande
                </button>
                <button
                  type="button"
                  onClick={() => setBulkPrecommande(false)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${!bulkPrecommande ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-700 border-slate-300"}`}
                  data-testid="bulk-type-rappel"
                >
                  📞 Simple rappel
                </button>
              </div>
              <p className="text-xs text-indigo-700 mt-2">Tu pourras modifier le type d&apos;un contact individuellement après import.</p>
            </div>

            {/* Aide format */}
            <div className="p-3 bg-blue-50 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-2">Collez directement vos données — 3 formats supportés :</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-blue-700">
                <div className="bg-white/60 p-2 rounded border border-blue-100">
                  <span className="font-semibold">Tableur (Tab)</span>
                  <p className="opacity-80 mt-0.5">Copie depuis Excel/Sheets</p>
                </div>
                <div className="bg-white/60 p-2 rounded border border-blue-100">
                  <span className="font-semibold">Pipe (|)</span>
                  <p className="opacity-80 mt-0.5">Nom | Tel | Qté</p>
                </div>
                <div className="bg-white/60 p-2 rounded border border-blue-100">
                  <span className="font-semibold">Texte libre</span>
                  <p className="opacity-80 mt-0.5">Nom Tel Qté</p>
                </div>
              </div>
            </div>

            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={`Collez ici — exemples :\n\n[Tableur] Marie Dupont\t0470123456\t2\n[Pipe]    Jean Martin | 0489111222 | 1\n[Texte]   Sophie Louis 0498333444 3`}
              rows={8}
              className="font-mono text-sm"
              data-testid="bulk-textarea"
            />

            {bulkText.trim() && (
              <div className="flex items-center gap-3">
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                  {bulkText.split(/\n/).filter(l => l.trim()).length} ligne(s) détectée(s)
                </Badge>
                {bulkSku && (
                  <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">
                    Produit : {produits.find(p => p.sku === bulkSku)?.modele} {produits.find(p => p.sku === bulkSku)?.couleur}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkImporting}>Annuler</Button>
            <Button onClick={submitBulk} disabled={bulkImporting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-submit-btn">
              {bulkImporting ? "Import…" : <><Upload className="w-4 h-4 mr-2" />Importer les précommandes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Rappels;
